// Guide: @asoftwareworld/card-validator
// Test card numbers: https://stripe.com/docs/testing#cards
// Test card numbers: https://www.paypalobjects.com/en_AU/vhelp/paypalmanager_help/credit_card_numbers.htm
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

// Define the CardValidation interface
interface CardValidation {
  name: string;
  patterns: Array<string>;
  numberPlaceholder: string;
  numberMask: string;
  cvvMask: string;
  cvvPlaceholder: string;
}

export function getMaskForEcheck(accountNumber: string, avoidLastDigits = 4) {
  let length = accountNumber.length || 0;
  let result = '';
  while (length > avoidLastDigits) {
    result += 'x';
    length--;
  }
  return result;
}

// Define the card types and their patterns
// (https://medium.com/hootsuite-engineering/a-comprehensive-guide-to-validating-and-formatting-credit-cards-b9fa63ec7863)
export const Issuer: Array<CardValidation> = [
  {
    name: 'americanexpress',
    patterns: ['34', '37'],
    numberPlaceholder: '1234 xxxx xxxx xxx',
    numberMask: '0000 0000 0000 000',
    cvvMask: '0000',
    cvvPlaceholder: 'xxxx',
  },
  {
    name: 'visa',
    patterns: ['4'],
    numberMask: '0000 0000 0000 0000',
    numberPlaceholder: '1234 xxxx xxxx xxxx',
    cvvMask: '000',
    cvvPlaceholder: 'xxx',
  },
  {
    name: 'mastercard',
    patterns: ['51', '52', '53', '54', '55'],
    numberMask: '0000 0000 0000 0000',
    numberPlaceholder: '1234 xxxx xxxx xxxx',
    cvvMask: '000',
    cvvPlaceholder: 'xxx',
  },
  {
    name: 'discover',
    patterns: ['6011', '622', '64', '65'],
    numberMask: '0000 0000 0000 0000',
    numberPlaceholder: '1234 xxxx xxxx xxxx',
    cvvMask: '000',
    cvvPlaceholder: 'xxx',
  },
  {
    name: 'dinersclub',
    patterns: ['300', '301', '302', '303', '304', '305', '36', '38'],
    numberMask: '0000 0000 0000 00',
    numberPlaceholder: '1234 xxxx xxxx xx',
    cvvMask: '000',
    cvvPlaceholder: 'xxx',
  },
  {
    name: 'jcb',
    patterns: ['1800', '2131', '35'],
    numberMask: '0000 0000 0000 0000',
    numberPlaceholder: '1234 xxxx xxxx xxxx',
    cvvMask: '000',
    cvvPlaceholder: 'xxx',
  },
  {
    name: 'maestro',
    patterns: [
      '5018',
      '5020',
      '5038',
      '5044',
      '5046',
      '5048',
      '5081',
      '6038',
      '6220',
      '6304',
      '6759',
      '6761',
      '6762',
      '6763',
      '6799',
    ],
    numberMask: '0000 0000 0000 0000',
    numberPlaceholder: '1234 xxxx xxxx xxxx',
    cvvMask: '000',
    cvvPlaceholder: 'xxx',
  },
];

export const luhnCheck = (cardNumber: string): boolean => {
  if (!cardNumber.length) {
    return false;
  }

  // Remove all whitespaces from card number.
  cardNumber = cardNumber.replace(/\s/g, '');

  // 1. Remove last digit;
  const lastDigit = Number(cardNumber[cardNumber.length - 1]);

  // 2. Reverse card number
  const reverseCardNumber = cardNumber
    .slice(0, cardNumber.length - 1)
    .split('')
    .reverse()
    .map((x) => Number(x));

  let sum = 0;

  // 3. + 4. Multiply by 2 every digit on odd position.
  // Subtract 9 if digit > 9
  for (let i = 0; i <= reverseCardNumber.length - 1; i += 2) {
    reverseCardNumber[i] = reverseCardNumber[i] * 2;
    if (reverseCardNumber[i] > 9) {
      reverseCardNumber[i] = reverseCardNumber[i] - 9;
    }
  }

  // 5. Make the sum of obtained values from step 4.
  sum = reverseCardNumber.reduce((acc, currValue) => acc + currValue, 0);

  // 6. Calculate modulo 10 of the sum from step 5 and the last digit.
  // If it's 0, you have a valid card number :)
  return (sum + lastDigit) % 10 === 0;
};

const getValidationConfigFromCardNo = (
  cardNumber: string,
): CardValidation | null =>
  Issuer.find((card) => {
    const patterns = card.patterns.map(
      (pattern) => new RegExp(`^${pattern}`, 'g'),
    );
    const matchResult = patterns
      .map((pattern) => cardNumber.match(pattern))
      .filter((result) => result);

    return !!matchResult.length;
  }) || null;

const invalidCreditCardNumber = (cardNumber: string): boolean => {
  const invalidValues = ['0', '1', '7', '8', '9'];
  const invalidValue = invalidValues.find((x) => cardNumber.startsWith(x));
  if (invalidValue) {
    return true;
  }
  return false;
};

export class CardValidationMask {
  static getCardValidationPattern(rawValue: string): CardValidation | null {
    return getValidationConfigFromCardNo(rawValue);
  }
}

export class CardValidator {
  static amexPattern = /^[0-9]{4}$/;
  static cardPattern = /^[0-9]{3}$/;
  static cardNumber(
    control: AbstractControl,
  ): { [key: string]: boolean } | null {
    const isValidCheck = luhnCheck(control.value);
    const invalidNumber = invalidCreditCardNumber(control.value);
    return isValidCheck && !invalidNumber ? null : { invalidCreditCard: true };
  }

  static getCvvPattern(cardNumber: string): RegExp {
    // Determine the card type based on the card number
    if (cardNumber.startsWith('37') || cardNumber.startsWith('34')) {
      return this.amexPattern; // AMEX CVV should have 4 digits
    }
    return this.cardPattern; // Other card types' CVV should have 3 digits
  }

  static getCvvPatternByCardType(cardType: string): RegExp {
    // Determine the card type based on the card number
    if (cardType === 'americanexpress') {
      return this.amexPattern; // AMEX CVV should have 4 digits
    }
    return this.cardPattern; // Other card types' CVV should have 3 digits
  }

  // CVV validator
  static cvvNumber(
    control: AbstractControl,
    cardNumberValue: string,
  ): { [key: string]: boolean } | null {
    if (control.value) {
      try {
        const cvvValue = control.value || '';

        // Check if the card number and CVV are empty
        if (!cardNumberValue || !cvvValue) {
          return null;
        }

        // Define the CVV pattern based on the card type
        const cvvPattern = this.getCvvPattern(cardNumberValue);

        // Validate CVV based on the pattern
        if (!cvvPattern.test(cvvValue)) {
          return { invalidCVV: true };
        }

        return null; // CVV is valid
      } catch (_) {
        return { invalidCVV: true };
      }
    }
    return null;
  }

  // Account Number validator
  static accountNumber(
    control: AbstractControl,
  ): { [key: string]: boolean } | null {
    if (control.value) {
      try {
        const accountValue = control.value || '';

        // Define the Account number pattern based on the card type
        const accountNumberPattern = /^[0-9]{5,18}$/;
        const allZero = /^0+$/;

        const accountValueStr = accountValue.toString();
        if (accountValueStr.length > 18) {
          return { invalidLengthAccountNumber: true };
        }
        // Validate Account number based on the pattern
        if (
          !accountNumberPattern.test(accountValue) ||
          allZero.test(accountValue)
        ) {
          return { invalidAccountNumber: true };
        }

        return null; // Account number is valid
      } catch (_) {
        return { invalidAccountNumber: true };
      }
    }
    return null;
  }

  // Account Number validator
  static routingNumber(
    control: AbstractControl,
  ): { invalidRoutingNumber: boolean } | null {
    if (control.value) {
      try {
        const routingValue = control.value || '';

        // Define the Account number pattern based on the card type
        const accountNumberPattern = /^[0-9]{9}$/;
        const allZero = /^0+$/;

        // Validate Account number based on the pattern
        if (
          !accountNumberPattern.test(routingValue) ||
          allZero.test(routingValue)
        ) {
          return { invalidRoutingNumber: true };
        }

        return null; // Account number is valid
      } catch (_) {
        return { invalidRoutingNumber: true };
      }
    }
    return null;
  }
  // CVV validator by card type
  static cvvNumberCardType(cardType?: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const cvvValue = control.value || '';
      try {
        // Check if the card number and CVV are empty
        if (!cardType || !cvvValue) {
          return null;
        }

        // Define the CVV pattern based on the card type
        const cvvPattern = this.getCvvPatternByCardType(cardType);

        // Validate CVV based on the pattern
        if (!cvvPattern.test(cvvValue)) {
          return { invalidCVV: true };
        }

        return null; // CVV is valid
      } catch (_) {
        return { invalidCVV: true };
      }
    };
  }
}
